/**
 * Drawing from a stock (D244; the Act II specification's §13.1d) — `take`
 * on a fixed thing the player takes FROM: the rack of canisters, the
 * emergency locker's one spare.
 *
 * @remarks
 * The take handler hands a stock here once the thing's own `takeRefusal`
 * has had its say (D199's order, unchanged: a pack says "not now" in the
 * thing's words before anything else is judged). The ladder:
 *
 * 1. **Which draw.** The first whose `when` holds answers the take; none,
 *    and the thing refuses as any fixed thing does (`notPortable`) — the
 *    stock is not there for this player, now.
 * 2. **Empty.** The draw's counter has reached its ceiling: everything it
 *    counts has been given. Its `emptyNarratives`, or the `stockEmpty` slot.
 * 3. **Full.** Something the draw gives would pass ITS counter's ceiling:
 *    the suit has four clips. Its `fullNarratives`, or the `stockFull`
 *    slot — and nothing moves, so no canister is drawn to be thrown away.
 * 4. **The draw.** The draw's counter moves one step, each gift by its
 *    amount — as effects, so a counter's `onMax` answers a draw exactly as
 *    it answers any other move — then the take's lines speak, reading the
 *    new amounts (`SPARES {counter:counter_spares} OF 4`), then the draw's
 *    own effects.
 *
 * The thing never enters the hands and no acquisition is written: a stock
 * is where things come from, not a thing that is had.
 *
 * @packageDocumentation
 */

import type { CounterId, EffectDefinition, ItemDefinition, ItemStock, RuntimeState, StockDraw } from '@orpheus/content-schema';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

import { requireCounter } from '../bundle/bundle-lookups.js';
import { evaluateCondition } from '../conditions/evaluate-condition.js';
import { speakSlot } from '../defaults/default-responses.js';
import { counterValueOf } from '../entities/counter-state.js';
import { type ActionHandlerResult, type CompletedAction, rejected, splitSpokenLines } from './action-handler-result.js';
import { stampNarrativeLines, type TranscriptCursor } from './transcript-cursor.js';
import { withEffects } from './with-effects.js';

/**
 * Is this counter at its declared ceiling — or would `by` more carry it past?
 *
 * @param bundle - The loaded bundle
 * @param state - The live runtime state
 * @param counterId - The counter
 * @param by - How much more is asked of it (zero asks whether it is already full)
 * @returns Whether the counter cannot take `by` more and still be inside its range
 */
const wouldPassCeiling = (bundle: EngineGameBundle, state: RuntimeState, counterId: CounterId, by: number): boolean =>
	counterValueOf(bundle, state, counterId) + by > requireCounter(bundle, counterId).max;

/**
 * The moves one draw makes, as the effects every amount is moved by: one
 * step on what has been drawn, and each gift by its amount.
 *
 * @param draw - The draw answering the take
 * @returns The counting effects, in order
 */
const countingEffectsOf = (draw: StockDraw): readonly EffectDefinition[] => [
	{ by: 1, counterId: draw.drawnCounterId, type: 'adjust_counter' },
	...draw.gives.map((gift): EffectDefinition => ({ by: gift.by, counterId: gift.counterId, type: 'adjust_counter' })),
];

/**
 * `take` on a stock — the ladder in the module remarks.
 *
 * @param bundle - The loaded bundle
 * @param state - The state before the turn
 * @param item - The thing taken from
 * @param stock - Its stock
 * @param cursor - This turn's transcript cursor
 * @returns The completed draw, or the typed refusal
 */
export const drawFromStock = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	item: ItemDefinition,
	stock: ItemStock,
	cursor: TranscriptCursor,
): ActionHandlerResult => {
	const draw = stock.draws.find((candidate) => candidate.when === undefined || evaluateCondition(candidate.when, bundle, state));

	if (draw === undefined) {
		return rejected(bundle, state, { itemId: item.itemId, type: 'not_portable' }, [speakSlot(bundle, state, 'notPortable')]);
	}

	// One step more would carry the drawn counter past its ceiling exactly when it is already there.
	if (wouldPassCeiling(bundle, state, draw.drawnCounterId, 1)) {
		return rejected(bundle, state, { itemId: item.itemId, type: 'stock_empty' }, draw.emptyNarratives ?? [speakSlot(bundle, state, 'stockEmpty', { itemName: item.name })]);
	}

	if (draw.gives.some((gift) => wouldPassCeiling(bundle, state, gift.counterId, gift.by))) {
		return rejected(bundle, state, { itemId: item.itemId, type: 'stock_full' }, draw.fullNarratives ?? [speakSlot(bundle, state, 'stockFull', { itemName: item.name })]);
	}

	const counted = withEffects(bundle, { cursor, entries: [], state, type: 'completed' }, countingEffectsOf(draw));
	const { lines, spokenSlots } = splitSpokenLines(draw.takeNarratives ?? [speakSlot(bundle, counted.state, 'takeAcknowledgement', { itemName: item.name })]);
	const stamped = stampNarrativeLines(bundle, counted.state, counted.cursor, lines, `take:${item.itemId}`);
	const spoken: CompletedAction = {
		...counted,
		cursor: stamped.cursor,
		entries: [...counted.entries, ...stamped.entries],
		...(spokenSlots.length === 0 ? {} : { spokenSlots }),
	};

	return withEffects(bundle, spoken, draw.effects);
};
